import { useMemo } from 'react'
import { FormattedMessage, IntlProvider } from 'react-intl'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Flex,
  Stack,
  Text,
} from '@chakra-ui/react'
import { format } from 'date-fns'
import { useRecoilState, useRecoilValue } from 'recoil'

import { FormAuthType, FormColorTheme, FormDto } from '~shared/types/form'

import { CITIZEN_ROOT_ROUTE } from '~constants/routes'
import Button from '~components/Button'

import { SubmissionData } from '~features/public-form/PublicFormContext'

import { LanguageFilter } from '../../../../../../atom'

export interface EndPageBlockProps {
  /** Form title of submission for display */
  formTitle: string
  endPage: FormDto['endPage']
  submissionData: SubmissionData
  colorTheme?: FormColorTheme
  authType?: FormAuthType
}

export const EndPageBlock = ({
  formTitle,
  endPage,
  submissionData,
  authType,
  colorTheme = FormColorTheme.Blue,
}: EndPageBlockProps): JSX.Element => {
  const prettifiedDateString = useMemo(() => {
    return format(new Date(submissionData.timeInEpochMs), 'dd MMM yyyy, h:mm a')
  }, [submissionData])

  const { formId } = useParams()
  const user = localStorage.getItem('user')
  const userData = user ? JSON.parse(user) : {}
  const locale: any = useRecoilValue(LanguageFilter)
  const navigate = useNavigate()
  const location = useLocation()
  return (
    <IntlProvider locale={locale}>
      <Flex flexDir="column">
        <Accordion allowToggle m="-1rem" flex={1} variant="medium">
          <AccordionItem color="secondary.500" border="none">
            <AccordionButton>
              <Stack
                direction="row"
                spacing="1rem"
                textAlign="start"
                justify="space-between"
                flex={1}
              >
                <Stack spacing="1rem">
                  <Text as="h2" textStyle="h2">
                    {locale == 'en'
                      ? `${endPage.title}`
                      : locale == 'si'
                      ? 'පෝරමය පිරවීම ගැන ඔබට ස්තුතියි.'
                      : 'படிவத்தை பூர்த்தி செய்ததற்கு நன்றி.'}
                  </Text>

                  {endPage.paragraph ? (
                    <Text color="secondary.500" textStyle="subhead-1">
                      {endPage.paragraph}
                    </Text>
                  ) : null}
                </Stack>
                <AccordionIcon />
              </Stack>
            </AccordionButton>
            <AccordionPanel textStyle="body-1" color="secondary.400">
              <Text textStyle="subhead-1" color="secondary.500">
                {formTitle}
              </Text>
              <Text>{submissionData.id}</Text>
              <Text>{prettifiedDateString}</Text>
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
        <Box mt="2.25rem">
          <Button
            as="a"
            onClick={() => {
              const currentPath = location.pathname
              if (currentPath === `/${formId}`) {
                // Refresh the page
                window.location.reload()
              } else {
                navigate(`/${formId}`, {
                  state: { tempLanguage: locale },
                })
              }
            }}
            variant="solid"
            colorScheme={`theme-${colorTheme}`}
          >
            {locale == 'en'
              ? `${endPage.buttonText}`
              : locale == 'si'
              ? 'තවත් පෝරමයක් ඉදිරිපත් කරන්න'
              : 'மற்றொரு படிவத்தை சமர்ப்பிக்கவும்'}
          </Button>
        </Box>
        {authType !== FormAuthType.NIL ||
        userData?._doc?.userRole?.code === 'DF_CITIZEN' ? (
          <Box mt="2.25rem">
            <Button
              onClick={() => {
                navigate('/citizen/dashboard', {
                  state: { tempLanguage: locale },
                })
              }}
              variant="solid"
              colorScheme={`theme-${colorTheme}`}
            >
              {locale == 'en'
                ? 'Return to dashboard'
                : locale == 'si'
                ? 'උපකරණ පුවරුව වෙත ආපසු යන්න'
                : 'திரும்ப டாஷ்போர்டு'}
            </Button>
          </Box>
        ) : null}
      </Flex>
    </IntlProvider>
  )
}
